///
/// EarthBrowser
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Keyboard shortcuts shared by both data dock variants (bottom drawer and
/// right dock): ⌘` toggles, Escape collapses (unless a modal is open),
/// Ctrl+Tab / Ctrl+Shift+Tab cycle items, ⌘1–9 select by index, ⌘W closes
/// the active item.
///

import { closeDrawerTab, setActiveDrawerTab, toggleDrawer } from "@/redux/dialogSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { useEffect } from "react"

export function useDockKeyboardShortcuts() {
	const dispatch = useAppDispatch()
	const { isOpen, tabs, activeTabId } = useAppSelector(state => state.dialog.bottomDrawer)

	useEffect(() => {
		if (tabs.length === 0) return

		const handleKeyDown = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key === "`") {
				e.preventDefault()
				dispatch(toggleDrawer())
				return
			}

			if (!isOpen) return

			if (e.key === "Escape") {
				// Only skip when a Radix (or similar) modal is actually open — closed
				// dialogs can stay mounted with role=dialog during exit
				const blockingModal = document.querySelector(
					'[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]'
				)
				if (!blockingModal) {
					e.preventDefault()
					e.stopPropagation()
					dispatch(toggleDrawer())
					return
				}
			}

			if (e.ctrlKey && e.key === "Tab") {
				e.preventDefault()
				const currentIndex = tabs.findIndex(t => t.windowId === activeTabId)
				const newIndex = e.shiftKey
					? (currentIndex <= 0 ? tabs.length - 1 : currentIndex - 1)
					: (currentIndex >= tabs.length - 1 ? 0 : currentIndex + 1)
				dispatch(setActiveDrawerTab(tabs[newIndex].windowId))
				return
			}

			if ((e.metaKey || e.ctrlKey) && e.key >= "1" && e.key <= "9") {
				const tabIndex = parseInt(e.key) - 1
				if (tabIndex < tabs.length) {
					e.preventDefault()
					dispatch(setActiveDrawerTab(tabs[tabIndex].windowId))
				}
				return
			}

			if ((e.metaKey || e.ctrlKey) && e.key === "w") {
				if (activeTabId) {
					e.preventDefault()
					dispatch(closeDrawerTab(activeTabId))
				}
			}
		}

		document.addEventListener("keydown", handleKeyDown)
		return () => document.removeEventListener("keydown", handleKeyDown)
	}, [tabs, isOpen, activeTabId, dispatch])
}
